import React, { useState } from 'react';
import { Star, EyeOff, Eye, X, Plus } from 'lucide-react';

// Tap a photo to make it the hero; the eye button hides/shows it on the listing page.
export const PhotoPicker = ({ photos, heroId, excluded, onHero, onToggleHidden }) => (
  <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2" data-testid="editor-photo-picker">
    {photos.map((p, i) => {
      const isHero = p.id === heroId;
      const hidden = excluded.includes(p.id);
      return (
        <div key={p.id} className={`relative aspect-[4/3] rounded-lg overflow-hidden ring-2 transition-all ${isHero ? 'ring-[#C6A14A]' : 'ring-transparent'}`}>
          <button type="button" onClick={() => onHero(p.id)} aria-label={`Make photo ${i + 1} the hero`} aria-pressed={isHero}
            data-testid={`editor-photo-${i}`} className="block h-full w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C6A14A]">
            <img src={p.url} alt="" loading="lazy" className={`h-full w-full object-cover transition-opacity ${hidden ? 'opacity-25 grayscale' : ''}`} />
          </button>
          {isHero && (
            <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 rounded-full bg-[#C6A14A] text-[#050816] text-[10px] font-semibold px-2 py-0.5" data-testid={`editor-hero-badge-${i}`}>
              <Star className="h-3 w-3 fill-current" />Hero
            </span>
          )}
          {!isHero && (
            <button type="button" onClick={() => onToggleHidden(p.id)} aria-label={hidden ? `Show photo ${i + 1}` : `Hide photo ${i + 1}`}
              data-testid={`editor-photo-hide-${i}`}
              className="absolute top-0.5 right-0.5 h-11 w-11 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80">
              {hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          )}
        </div>
      );
    })}
  </div>
);

export const HighlightsInput = ({ items, onChange }) => {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim().slice(0, 40);
    if (v && !items.includes(v) && items.length < 20) onChange([...items, v]);
    setDraft('');
  };
  return (
    <div className="space-y-3" data-testid="editor-highlights">
      <div className="flex flex-wrap gap-2">
        {items.map((h, i) => (
          <span key={h} className="inline-flex items-center gap-1 rounded-full border border-[#C6A14A]/40 bg-[#C6A14A]/10 pl-3 pr-1 py-1 text-sm text-[#E8E4DE]" data-testid={`editor-highlight-${i}`}>
            {h}
            <button type="button" onClick={() => onChange(items.filter((x) => x !== h))} aria-label={`Remove ${h}`} className="h-11 w-11 -my-2 rounded-full hover:bg-white/10 flex items-center justify-center" data-testid={`editor-highlight-remove-${i}`}>
              <X className="h-3.5 w-3.5" />
            </button>
          </span>
        ))}
        {!items.length && <span className="text-sm text-[#E8E4DE]/40">No highlights yet</span>}
      </div>
      <div className="flex gap-2">
        <input value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={40} placeholder="e.g. Mountain views"
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} aria-label="New highlight"
          className="flex-1 min-w-0 h-11 rounded-lg bg-[#0f1724] border border-white/15 px-3 text-white placeholder:text-white/30 focus:border-[#C6A14A] outline-none" data-testid="editor-highlight-input" />
        <button type="button" onClick={add} className="h-11 px-4 shrink-0 rounded-lg border border-[#C6A14A]/50 text-[#C6A14A] hover:bg-[#C6A14A]/10 inline-flex items-center gap-1" data-testid="editor-highlight-add">
          <Plus className="h-4 w-4" />Add
        </button>
      </div>
    </div>
  );
};
